// Many-to-many grouping for a single-value dimension: markets, creative formats.
//
// Same model as channel groups — one bucket, members on both sides — but a channel needs
// two keys (channel + publisher) where these need one, so this is its own component rather
// than a parameter on that one. The grouping is genuinely many-to-many in both directions:
// an actuals "GCC" line can cover UAE, KSA and Qatar in Statex, and one Statex format can
// arrive under several names across sources.

import { useState, useEffect, useCallback } from 'react';
import { cmApi } from '../../api.js';
import { useClient } from '../../ClientContext.jsx';
import { T } from '../../../../shared/styles/tokens.js';

const SOURCE_LABEL = {
  paid_media: 'Paid Media', offline: 'Offline',
  direct_buys: 'Direct Buys', manual: 'Manual',
};

export default function ValueGroupsSection({ kind, noun, help }) {
  const { clientId } = useClient();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState(null);

  const load = useCallback(() => {
    if (!clientId) return;
    setLoading(true);
    cmApi.getValueGroups(clientId, kind)
      .then(setData)
      .catch(e => setErr(e.message))
      .finally(() => setLoading(false));
  }, [clientId, kind]);

  useEffect(load, [load]);
  useEffect(() => { setDraft(null); }, [kind]);

  const toggle = (field, value) => setDraft(d => {
    const next = new Set(d[field]);
    next.has(value) ? next.delete(value) : next.add(value);
    return { ...d, [field]: next };
  });

  const save = async () => {
    setBusy(true); setErr(null);
    try {
      await cmApi.saveValueGroup(clientId, kind, {
        group_key: draft.group_key.trim(),
        statex: [...draft.statex],
        actuals: [...draft.actuals],
        replaces: draft.replaces,
      });
      setDraft(null);
      load();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  const remove = async (g) => {
    setBusy(true); setErr(null);
    try {
      await cmApi.deleteValueGroup(clientId, kind, { group_key: g.group_key });
      load();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  if (!clientId) return <p className="o-state">Select a client to map {noun}.</p>;
  if (loading) return <p className="o-state">Loading…</p>;

  const groups = data?.groups || [];
  const allStatex = data?.all_statex || [];
  const allActuals = data?.all_actuals || [];
  const looseStatex = data?.unassigned_statex || [];
  const looseActuals = data?.unassigned_actuals || [];

  const ownerOf = (field, value) => groups.find(g =>
    g.group_key !== draft?.replaces
    && g[field].some(x => x.value === value))?.group_key;

  const chip = (field, row) => {
    const on = draft[field].has(row.value);
    const owner = !on && ownerOf(field, row.value);
    // The source stays in the tooltip rather than the label: it is the same value on
    // nearly every chip, so inline it was noise rather than information.
    const source = row.source ? (SOURCE_LABEL[row.source] || row.source) : null;
    const hint = [source, owner && `currently in ${owner} — selecting moves it here`]
      .filter(Boolean).join(' · ');
    return (
      <button key={`${row.source || 'statex'}|${row.value}`} type="button"
        className="o-chip o-wide" aria-pressed={on}
        onClick={() => toggle(field, row.value)}
        title={hint || undefined}
        style={{ opacity: owner ? 0.55 : 1 }}>
        {row.value} · {Number(row.rows).toLocaleString()}
      </button>
    );
  };

  return (
    <div>
      <p className="o-note">{help}</p>

      {err && <div className="o-state o-error" style={{ textAlign: 'left', padding: `${T.s2}px 0` }}>{err}</div>}

      {(looseStatex.length > 0 || looseActuals.length > 0) && !draft && (
        <p className="o-hint">
          <b>{looseStatex.length} Statex and {looseActuals.length} actual-spend value
          {looseActuals.length === 1 ? '' : 's'} are in no group.</b> They only match an
          identical spelling on the other side, so most will not contribute to a factor.
        </p>
      )}

      <div className="o-head-row" style={{ marginBottom: T.s4 }}>
        <p className="o-eyebrow">Groups · {groups.length}</p>
        {!draft && (
          <button className="o-btn o-btn-primary"
            onClick={() => setDraft({ group_key: '', statex: new Set(), actuals: new Set(), replaces: null })}>
            New group
          </button>
        )}
      </div>

      {draft && (
        <div className="o-panel" style={{ padding: `${T.s4}px ${T.s5}px`, marginBottom: T.s5 }}>
          <div className="o-ctrl-row" style={{ padding: 0, border: 0, marginBottom: T.s4 }}>
            <div className="o-ctrl" style={{ flex: 1, minWidth: 240 }}>
              <label className="o-field-lbl">Group name</label>
              <input className="o-control" value={draft.group_key}
                onChange={e => setDraft(d => ({ ...d, group_key: e.target.value }))} />
            </div>
            <div className="o-ctrl" style={{ justifyContent: 'flex-end' }}>
              <div style={{ display: 'flex', gap: T.s2 }}>
                <button className="o-btn o-btn-primary" onClick={save}
                  disabled={busy || !draft.group_key.trim()
                            || (!draft.statex.size && !draft.actuals.size)}>
                  {busy ? 'Saving…' : 'Save group'}
                </button>
                <button className="o-btn" onClick={() => setDraft(null)}>Cancel</button>
              </div>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: T.s5 }}>
            <div>
              <p className="o-eyebrow" style={{ marginBottom: T.s2 }}>
                Statex competitive · {draft.statex.size} selected
              </p>
              <div className="o-chipset">{allStatex.map(s => chip('statex', s))}</div>
            </div>
            <div>
              <p className="o-eyebrow" style={{ marginBottom: T.s2 }}>
                Actual spend · {draft.actuals.size} selected
              </p>
              <div className="o-chipset">{allActuals.map(a => chip('actuals', a))}</div>
            </div>
          </div>

          <p className="o-note" style={{ margin: `${T.s3}px 0 0` }}>
            Dimmed values already belong to another group; selecting one moves it here.
          </p>
        </div>
      )}

      <div className="o-panel" style={{ overflowX: 'auto' }}>
        <table className="o-tbl">
          <thead>
            <tr>
              <th>Group</th><th>Statex competitive</th><th>Actual spend</th>
              <th>Rows</th><th></th>
            </tr>
          </thead>
          <tbody>
            {groups.length === 0 && (
              <tr><td colSpan={5} style={{ textAlign: 'center', color: T.text3 }}>
                No groups yet. Values only match where both sides spell them identically.
              </td></tr>
            )}
            {groups.map(g => (
              <tr key={g.group_key}>
                <td className="o-strong">
                  {g.group_key}
                  {!g.client_specific && (
                    <span className="o-badge o-grey" style={{ marginLeft: 6 }}>default</span>
                  )}
                </td>
                <td>
                  {g.statex.length === 0
                    ? <span style={{ color: T.text3 }}>none — no competitive side</span>
                    : g.statex.map(s => (
                        <span key={s.value} className="o-badge o-grey" style={{ marginRight: 4 }}>
                          {s.value}
                        </span>))}
                </td>
                <td>
                  {g.actuals.length === 0
                    ? <span style={{ color: T.text3 }}>none — nothing to calibrate from</span>
                    : g.actuals.map(a => (
                        <span key={`${a.source}|${a.value}`} className="o-badge o-grey"
                              style={{ marginRight: 4 }}>{a.value}</span>))}
                </td>
                <td className="o-num" style={{ whiteSpace: 'nowrap' }}>
                  {Number(g.statex_rows).toLocaleString()} / {Number(g.actual_rows).toLocaleString()}
                </td>
                <td className="o-acts">
                  <button className="o-btn o-btn-sm" disabled={busy}
                    onClick={() => setDraft({
                      group_key: g.group_key,
                      statex: new Set(g.statex.map(s => s.value)),
                      actuals: new Set(g.actuals.map(a => a.value)),
                      replaces: g.group_key,
                    })}>Edit</button>
                  {g.client_specific && (
                    <button className="o-btn o-btn-sm" disabled={busy}
                      onClick={() => remove(g)}>Delete</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {(looseStatex.length > 0 || looseActuals.length > 0) && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: T.s5, marginTop: T.s5 }}>
          <div>
            <p className="o-eyebrow" style={{ marginBottom: T.s2 }}>
              Ungrouped Statex · {looseStatex.length}
            </p>
            <div className="o-chipset">
              {looseStatex.map(s => (
                <span key={s.value} className="o-badge o-grey">
                  {s.value} · {Number(s.rows).toLocaleString()}
                </span>))}
            </div>
          </div>
          <div>
            <p className="o-eyebrow" style={{ marginBottom: T.s2 }}>
              Ungrouped actual spend · {looseActuals.length}
            </p>
            <div className="o-chipset">
              {looseActuals.map(a => (
                <span key={`${a.source}|${a.value}`} className="o-badge o-grey">
                  {a.value} · {Number(a.rows).toLocaleString()}
                </span>))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
